import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import MicNoneIcon from '@mui/icons-material/MicNone';

const normalizeValue = (value) => {
  if (typeof value === 'string') {
    return value;
  }

  return String(value?.text ?? '');
};

const simpleNotesTextFieldSx = {
  '& .MuiInputBase-input': {
    fontWeight: 400,
  },
  '& textarea': {
    fontWeight: 400,
  },
  '& input': {
    fontWeight: 400,
  },
};

const SimpleNotesInput = ({
  value = '',
  onDataChange = null,
  clearData = null,
  placeholder = 'Add notes',
  hintText = '',
  hintIcon = MicNoneIcon,
  minRows = 4,
  maxRows = undefined,
  textFieldSx = {},
  label = null,
  templateText = '',
}) => {
  const [text, setText] = useState(() => normalizeValue(value));
  const latestOnDataChangeRef = useRef(onDataChange);
  const latestExternalTextRef = useRef(normalizeValue(value));
  const lastClearDataRef = useRef(clearData);

  useEffect(() => {
    latestOnDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    const nextText = normalizeValue(value);
    if (nextText !== latestExternalTextRef.current) {
      latestExternalTextRef.current = nextText;
      setText(nextText);
    }
  }, [value, value?.text]);

  useEffect(() => {
    if (clearData == null || clearData === lastClearDataRef.current) {
      return;
    }

    lastClearDataRef.current = clearData;
    latestExternalTextRef.current = '';
    setText('');
  }, [clearData]);

  useEffect(() => {
    if (typeof templateText === 'string' && templateText.trim()) {
      latestExternalTextRef.current = templateText;
      setText(templateText);
    }
  }, [templateText]);

  const handleChange = (event) => {
    const nextText = event.target.value;
    latestExternalTextRef.current = nextText;
    setText(nextText);

    if (typeof latestOnDataChangeRef.current === 'function') {
      latestOnDataChangeRef.current({
        text: nextText,
        mediaFile: null,
        audioBlob: null,
      });
    }
  };

  return (
    <Box>
      {label ? (
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
          {label}
        </Typography>
      ) : null}

      {hintText ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            color: 'text.secondary',
            mb: 0.75,
          }}
        >
          {hintIcon ? (
            <Box
              component={hintIcon}
              sx={{ fontSize: '0.95rem', flexShrink: 0 }}
            />
          ) : null}
          <Typography sx={{ fontSize: '0.74rem', fontWeight: 600 }}>
            {hintText}
          </Typography>
        </Box>
      ) : null}

      <TextField
        fullWidth
        multiline
        minRows={minRows}
        maxRows={maxRows}
        value={text}
        placeholder={placeholder}
        onChange={handleChange}
        sx={Array.isArray(textFieldSx)
          ? [simpleNotesTextFieldSx, ...textFieldSx]
          : [simpleNotesTextFieldSx, textFieldSx]}
      />
    </Box>
  );
};

export default React.memo(SimpleNotesInput);
